import React, { useEffect, useState, useCallback } from "react";
import { Header, Footer, PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Users, ChatCircleDots, CurrencyInr, Crown, Sparkle, Pulse } from "@phosphor-icons/react";
import AIFallbackWidget from "../components/AIFallbackWidget";

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");

// Friendly labels for the raw tool_id keys
const TOOL_LABELS = {
  bhrigu: "Bhrigu Bindu",
  chakras: "Chakra Heat-Map",
  nakshatra: "Nakshatra Analysis",
  compatibility: "Compatibility · Guna Milan",
  wealth: "Wealth Quotient",
  rahu: "Rahu Placement",
  planet_effects: "9-Planet Effects",
  navamsa: "D9 Navamsa",
  numerology_deep: "Deep Numerology",
  varshaphal: "Varshaphal (Yearly)",
  past_life: "Past Life · D60",
  name_numerology: "Name Numerology",
};

const PERIODS = [
  { id: "24h", label: "24h" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "90d", label: "Last 90 days" },
  { id: "all", label: "All time" },
  { id: "custom", label: "Custom range" },
];

const Card = ({ label, value, sub, icon: Icon, accent = "amber", testid }) => (
  <div
    className={`rounded-2xl p-5 sm:p-6 bg-${accent}-50 border border-${accent}-200`}
    data-testid={testid}
  >
    <div className="flex items-start justify-between">
      <div className="label-eyebrow text-[#7C2D12]">{label}</div>
      {Icon && <Icon size={18} weight="duotone" className="text-amber-700 opacity-70" />}
    </div>
    <div className="font-serif-display text-3xl sm:text-4xl mt-3 font-light text-[#3F2D1B]" data-testid={`${testid}-value`}>{value}</div>
    {sub && <div className="text-xs text-muted mt-2 font-light">{sub}</div>}
  </div>
);

const Section = ({ title, children, right }) => (
  <section className="space-y-4">
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="label-eyebrow text-muted">{title}</div>
      {right}
    </div>
    {children}
  </section>
);

export default function AdminAnalyticsPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setRefreshing(true);
      try {
        const params = { period };
        if (period === "custom") {
          if (!customStart) return;
          params.start = customStart;
          if (customEnd) params.end = customEnd;
        }
        const r = await api.get("/admin/analytics", { params });
        setData(r.data);
        setErr(null);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      } finally {
        setRefreshing(false);
      }
    },
    [period, customStart, customEnd]
  );

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    if (!user.is_super_admin) { navigate("/"); return; }
    load();
  }, [loading, user, navigate, load]);

  // Live-refresh the online-users panel every 30s
  useEffect(() => {
    if (!user?.is_super_admin) return;
    const id = setInterval(() => load(true), 30000);
    return () => clearInterval(id);
  }, [user, load]);

  if (loading || (!data && !err)) {
    return (
      <PageShell>
        <Header />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 text-center text-muted">Loading analytics…</div>
        <Footer />
      </PageShell>
    );
  }

  if (err) {
    return (
      <PageShell>
        <Header />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 text-center text-rose-700" data-testid="analytics-error">
          {err}
        </div>
        <Footer />
      </PageShell>
    );
  }

  const { users, engagement, revenue, tool_unlocks, astrologers, reviews, daily_signups, vip, visitors } = data;
  const maxSignup = Math.max(1, ...daily_signups.map((d) => d.count));
  const maxTopReport = Math.max(1, ...tool_unlocks.top_5_reports.map((r) => r.count));

  const periodSelector = (
    <div className="flex items-center flex-wrap gap-2" data-testid="period-selector">
      {PERIODS.map((p) => (
        <button
          key={p.id}
          onClick={() => setPeriod(p.id)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
            period === p.id ? "bg-[#3F2D1B] text-white border-[#3F2D1B]" : "bg-white text-[#3F2D1B] border-soft hover:border-amber-400"
          }`}
          data-testid={`period-${p.id}`}
        >
          {p.label}
        </button>
      ))}
      {period === "custom" && (
        <div className="flex items-center gap-2 ml-1">
          <input
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            className="text-xs px-2 py-1.5 rounded-md border border-soft bg-white"
            data-testid="custom-start"
          />
          <span className="text-muted text-xs">→</span>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            className="text-xs px-2 py-1.5 rounded-md border border-soft bg-white"
            data-testid="custom-end"
          />
          <button
            onClick={() => load()}
            disabled={!customStart}
            className="btn-primary rounded-full px-3 py-1.5 text-xs font-medium disabled:opacity-50"
            data-testid="apply-custom"
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );

  return (
    <PageShell>
      <Header />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20 space-y-12">
        <header>
          <div className="label-eyebrow text-muted">Super-Admin · App Analytics</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl mt-3 font-light leading-tight">Pulse of the cosmos</h1>
          <p className="text-muted text-sm mt-3 font-light">
            Window: <span className="font-medium text-[#3F2D1B]" data-testid="active-period">{data.period}</span>{" · "}
            Refreshed {new Date(data.generated_at).toLocaleString()}{refreshing && " · loading…"}
          </p>
          <div className="mt-5">{periodSelector}</div>
        </header>

        {/* ONLINE NOW — pulse card */}
        <Section title="Live · right now">
          <div className="rounded-2xl p-5 sm:p-7 bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="relative flex h-3 w-3 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-600" />
              </div>
              <div className="font-serif-display text-3xl sm:text-4xl font-light text-[#1F3F2D]" data-testid="online-now">
                {fmtNum(users.online_now)} <span className="text-base text-muted">online</span>
              </div>
              <div className="text-xs text-muted">(active in the last 5 minutes)</div>
            </div>
            {users.online_preview.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2" data-testid="online-preview">
                {users.online_preview.map((u) => (
                  <div key={u.user_id} className="flex items-center gap-2 bg-white/70 rounded-full pl-1 pr-3 py-1 border border-emerald-200" title={u.email}>
                    {u.picture ? (
                      <img src={u.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-emerald-200 flex items-center justify-center text-[10px] font-medium text-[#1F3F2D]">
                        {(u.name || u.email || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-xs font-medium">{u.name || u.email}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Section>

        {/* AI REPLY HEALTH — fallback rate, broken down per astrologer */}
        <AIFallbackWidget />

        <Section title="Users">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card label="Unique visitors · lifetime" value={fmtNum(visitors.lifetime_unique)} sub={`${visitors.total_pageviews} total pageviews`} icon={Pulse} testid="uv-lifetime" />
            <Card label="Unique visitors in window" value={fmtNum(visitors.in_range)} sub={`${visitors.new_in_range} new · ${visitors.today} today · ${visitors.last_7d} in 7d`} icon={Pulse} testid="uv-range" />
            <Card label="Signup conversion" value={`${visitors.signup_conversion_pct}%`} sub={`${users.total} signed up of ${visitors.lifetime_unique} visitors`} icon={Sparkle} testid="uv-conv" />
            <Card label="Total signed-up users" value={fmtNum(users.total)} sub={`${users.new_in_range} new in window`} icon={Users} testid="users-total" />
            <Card label="DAU · daily active" value={fmtNum(users.dau)} sub={`${users.sessions_today} sessions today`} icon={Users} testid="users-dau" />
            <Card label="MAU · monthly active" value={fmtNum(users.mau)} sub={`${users.with_birth_details} have birth details`} icon={Pulse} testid="users-mau" />
            <Card label="VIP active" value={fmtNum(users.vip_active)} sub="Inside their 7-day unlock window" icon={Crown} testid="users-vip" />
          </div>
        </Section>

        <Section title="Engagement">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card label="Chats in window" value={fmtNum(engagement.chats_in_range)} sub={`Lifetime ${engagement.total_chats} · today ${engagement.chats_today}`} icon={ChatCircleDots} accent="rose" testid="chats-in-range" />
            <Card label="Active now" value={fmtNum(engagement.chats_active_now)} sub="Currently open chats" icon={ChatCircleDots} accent="rose" testid="chats-active" />
            <Card label="Total messages" value={fmtNum(engagement.total_messages)} icon={ChatCircleDots} accent="rose" testid="msg-total" />
            <Card label="Reports saved" value={fmtNum(engagement.total_reports_saved)} sub="Across all premium tools" icon={Sparkle} accent="rose" testid="reports-saved" />
          </div>
        </Section>

        <Section title="Revenue">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card label="Revenue in window" value={fmtINR(revenue.revenue_in_range_inr)} sub={`${revenue.orders_in_range} paid orders`} icon={CurrencyInr} accent="emerald" testid="rev-range" />
            <Card label="Lifetime revenue" value={fmtINR(revenue.total_inr)} sub={`${revenue.paid_orders} total orders`} icon={CurrencyInr} accent="emerald" testid="rev-total" />
            <Card label="Avg. order value" value={fmtINR(revenue.avg_order_value)} icon={CurrencyInr} accent="emerald" testid="rev-aov" />
          </div>
        </Section>

        {/* VIP King — currently active + lifetime breakdown */}
        <Section title="VIP King · ₹4999 pass">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card label="VIP active NOW" value={fmtNum(vip.active_now)} sub="Inside the 7-day unlock window" icon={Crown} accent="amber" testid="vip-active-now" />
            <Card label="Lifetime VIP buyers" value={fmtNum(vip.lifetime_buyers)} sub={`${vip.expired} expired · ${vip.paid_orders} paid orders`} icon={Crown} accent="amber" testid="vip-lifetime" />
            <Card label="VIP revenue" value={fmtINR(vip.revenue_inr)} sub="Lifetime VIP-pass sales" icon={CurrencyInr} accent="amber" testid="vip-revenue" />
            <Card label="Wallet credit outstanding" value={fmtINR(vip.wallet_credit_outstanding_inr)} sub="Still owed to active VIPs" icon={CurrencyInr} accent="amber" testid="vip-credit-outstanding" />
          </div>
          {vip.active_preview.length > 0 && (
            <div className="rounded-2xl p-5 sm:p-6 bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-300" data-testid="vip-active-preview">
              <div className="label-eyebrow text-[#7C2D12] mb-4">Active VIPs · who & how long left</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {vip.active_preview.map((v) => (
                  <div key={v.user_id} className="flex items-center gap-3 bg-white/70 rounded-xl px-3 py-2.5 border border-amber-200" data-testid={`vip-row-${v.user_id}`}>
                    {v.picture ? (
                      <img src={v.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-amber-300 flex items-center justify-center text-sm font-medium text-[#7C2D12]">
                        {(v.name || v.email || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{v.name || v.email}</div>
                      <div className="text-xs text-muted truncate">{v.email}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-medium text-amber-800">{v.days_left}d left</div>
                      <div className="text-[10px] text-muted">{fmtINR(v.vip_credit_remaining)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* TOP 5 REPORTS PURCHASED — period-aware */}
        <Section title="Top 5 reports purchased · in window">
          <div className="rounded-2xl p-5 sm:p-7 bg-[#FFF6E0] border border-amber-200" data-testid="top-5-reports">
            {tool_unlocks.top_5_reports.length === 0 ? (
              <div className="text-muted text-sm">No reports purchased in this window.</div>
            ) : (
              <div className="space-y-3.5">
                {tool_unlocks.top_5_reports.map((r, idx) => (
                  <div key={r.tool_id} className="space-y-1.5" data-testid={`top-report-${idx}`}>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2.5">
                        <span className="font-serif-display text-2xl font-light text-amber-700 w-7 text-center">#{idx + 1}</span>
                        <span className="font-medium">{TOOL_LABELS[r.tool_id] || r.tool_id}</span>
                      </div>
                      <div className="flex items-baseline gap-3 text-xs text-muted">
                        <span>{r.last_at ? `last: ${new Date(r.last_at).toLocaleDateString()}` : ""}</span>
                        <span className="font-medium text-[#3F2D1B] text-sm tabular-nums">{fmtNum(r.count)} sold</span>
                      </div>
                    </div>
                    <div className="h-2.5 w-full bg-amber-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500 rounded-full transition-all"
                        style={{ width: `${(r.count / maxTopReport) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-5 pt-4 border-t border-amber-200 grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-muted">Total in window</div>
                <div className="font-medium text-[#3F2D1B] text-base mt-0.5">{fmtNum(tool_unlocks.in_range)}</div>
              </div>
              <div>
                <div className="text-muted">Lifetime unlocks</div>
                <div className="font-medium text-[#3F2D1B] text-base mt-0.5">{fmtNum(tool_unlocks.total)}</div>
              </div>
            </div>
          </div>
        </Section>

        {/* Full tool breakdown */}
        {tool_unlocks.by_tool.length > 5 && (
          <Section title="Full tool breakdown · in window">
            <div className="rounded-2xl p-5 sm:p-6 bg-white border border-soft space-y-2">
              {tool_unlocks.by_tool.map((r) => (
                <div key={r.tool_id} className="flex items-center justify-between text-sm py-1" data-testid={`unlock-row-${r.tool_id}`}>
                  <span className="font-medium">{TOOL_LABELS[r.tool_id] || r.tool_id}</span>
                  <span className="text-muted">{fmtNum(r.count)}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section title="Top astrologers · in window">
          <div className="rounded-2xl p-5 sm:p-6 bg-[#FFF6E0] border border-amber-200 space-y-2.5" data-testid="top-astrologers">
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2 text-xs text-muted label-eyebrow">
              <div>Name</div>
              <div className="hidden md:block">Technique</div>
              <div>Type</div>
              <div className="hidden md:block text-right">Rate</div>
              <div className="text-right">Chats</div>
            </div>
            {astrologers.top_by_chats.length === 0 && <div className="text-muted text-sm">No chats yet in this window.</div>}
            {astrologers.top_by_chats.map((a) => (
              <div key={a.astrologer_id} className="grid grid-cols-3 md:grid-cols-5 gap-2 items-center text-sm py-1.5 border-t border-amber-100" data-testid={`top-astro-${a.astrologer_id}`}>
                <div className="font-medium truncate">{a.name}</div>
                <div className="hidden md:block text-muted truncate">{a.technique}</div>
                <div className="text-muted text-xs">{a.is_ai ? "AI" : "Human"}</div>
                <div className="hidden md:block text-right text-xs text-muted">{fmtINR(a.rate_per_min)}/min</div>
                <div className="text-right font-medium">{fmtNum(a.chats)}</div>
              </div>
            ))}
            <div className="text-xs text-muted pt-3 border-t border-amber-100">
              Total astrologers: <span className="font-medium">{astrologers.total}</span> ·{" "}
              AI {astrologers.ai} · Human {astrologers.human} · Reviews {reviews.total} · Avg ⭐{reviews.avg_rating || "—"}
            </div>
          </div>
        </Section>

        {daily_signups.length > 0 && (
          <Section title="Daily signups · last 14 days">
            <div className="rounded-2xl p-5 sm:p-6 bg-white border border-soft">
              <div className="flex items-end gap-1.5 h-32" data-testid="signups-chart">
                {daily_signups.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5" title={`${d.date}: ${d.count}`}>
                    <div className="text-[10px] text-muted font-medium">{d.count}</div>
                    <div
                      className="w-full bg-amber-400 rounded-t-sm"
                      style={{ height: `${(d.count / maxSignup) * 100}%`, minHeight: "4px" }}
                    />
                    <div className="text-[9px] text-muted">{d.date.slice(5)}</div>
                  </div>
                ))}
              </div>
            </div>
          </Section>
        )}
      </div>
      <Footer />
    </PageShell>
  );
}
